import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'

export default function Terms() {
  useEffect(() => {
    document.body.classList.add('v2-legal-page')
    return () => document.body.classList.remove('v2-legal-page')
  }, [])

  return (
    <>
      <SEO
        title="Terms of Service - tokendance.net"
        description="tokendance.net terms of service for FDE work, LLM infrastructure, and production AI services."
        path="/terms"
      />

      <div className="od-home od-legal-shell">
        <SiteNav />

        <main className="od-legal-main">
          <header className="od-legal-hero">
            <h1>Terms of Service</h1>
            <p>Last updated: April 27, 2026</p>
          </header>

          <div className="od-legal-sections">
            <section className="od-legal-section">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing or using the tokendance.net platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service. These Terms apply to all users, including visitors, registered users, paying subscribers, and any AI coding agents acting on your behalf.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>2. Description of Service</h2>
              <p>
                tokendance.net provides forward-deployed engineering, LLM infrastructure, and production AI services. We help teams design, build, evaluate, deploy, and operate AI systems, including model APIs, agent workflows, inference services, evaluation loops, and operational runbooks.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>3. Account Registration and Agent Tokens</h2>
              <ul>
                <li>You must provide accurate and complete information when creating an account.</li>
                <li>You are responsible for maintaining the security of your account credentials and any API tokens you generate.</li>
                <li>API tokens you issue to AI coding agents authenticate as you. Any deployment, configuration change, or resource consumption performed with a valid token is treated as your action.</li>
                <li>You are responsible for all activities that occur under your account, including those initiated by agents holding your tokens.</li>
                <li>You must notify us immediately of any unauthorized use of your account or any leaked, lost, or compromised credential. You may request credential rotation or revocation for integrations we support.</li>
                <li>You must be at least 16 years of age to create an account.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>4. Agent Actions and Operator Responsibility</h2>
              <p>
                You are the accountable party for everything your AI coding agent does on tokendance.net. The agent is the operator; you are the principal who pays the bill, sets the budget, and holds the safety controls (rollback, cancel, revoke token, set spend cap).
              </p>
              <ul>
                <li><strong>We are not responsible for the actions, decisions, code, or output of your AI coding agent.</strong> This includes deployments your agent triggers, infrastructure it provisions, environment variables it sets, dependencies it installs, secrets it reads or writes, code it pushes, or any spend it incurs.</li>
                <li>We do not review, validate, or audit the prompts, plans, tools, or generated artifacts produced by third-party AI coding agents before they reach our platform. The agent runs in your environment, under your authority, with your token.</li>
                <li>If an agent makes an unintended deployment, deletes a resource, leaks a secret into logs, exhausts your budget, or otherwise causes loss, the responsibility lies with you and the agent's vendor — not tokendance.net. You are expected to use the safety controls we provide (token scoping, spend caps, manual confirmation prompts, rollback) to constrain agent behavior.</li>
                <li>We provide the safety surfaces (read-only token modes, spend caps, deploy approval prompts where supported, rollback, audit log) on a best-effort basis. They reduce blast radius but do not eliminate it. You are expected to configure them.</li>
                <li>If you suspect an agent or integration is misbehaving — acting outside its instructions, draining spend, or exfiltrating data — you should immediately revoke the relevant credential and contact us at <a href="mailto:hi@tokendance.net">hi@tokendance.net</a>.</li>
                <li>You agree not to hold tokendance.net liable for outcomes attributable to third-party agent behavior, agent vendor outages, prompt-injection attacks, or third-party model output except as required by law or an applicable written agreement.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>5. User Content and Intellectual Property</h2>
              <p>
                You retain all ownership rights to the source code, assets, configuration, and content you (or your agent) push to tokendance.net. By pushing content, you grant us a limited, non-exclusive license to process, build, and deploy that content solely for the purpose of providing the Service. We do not claim any intellectual property rights over your content and we do not use your code to train models.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>6. Acceptable Use</h2>
              <p>You agree not to use the Service — directly, or via an agent acting on your behalf — to:</p>
              <ul>
                <li>Deploy applications that violate any applicable laws or regulations.</li>
                <li>Host or distribute malware, phishing sites, or other malicious content.</li>
                <li>Engage in cryptocurrency mining without explicit authorization.</li>
                <li>Conduct denial-of-service attacks or other activities that disrupt the Service.</li>
                <li>Attempt to gain unauthorized access to other users' deployments or data.</li>
                <li>Infringe upon the intellectual property rights of others.</li>
                <li>Deploy content that promotes violence, harassment, or discrimination.</li>
                <li>Use the platform to send unsolicited bulk communications (spam).</li>
                <li>Configure agents in a way that intentionally evades our rate limits, abuse controls, or spend caps.</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate accounts (and revoke their agent tokens) that violate these restrictions without prior notice. Agent-initiated violations are treated as account-holder violations. For the complete rules, see our <a href="/aup">Acceptable Use Policy</a>, which is incorporated into these Terms.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>7. Pricing and Payment</h2>
              <ul>
                <li>Free tier usage is subject to resource and spend limits described in your plan or service agreement.</li>
                <li>Paid plans are billed monthly or annually as selected at the time of purchase, plus metered usage (compute, bandwidth, storage, AI gateway tokens) in excess of plan limits.</li>
                <li>Agent-initiated usage counts toward your plan and metered charges. We strongly recommend setting a spend cap before issuing tokens to agents.</li>
                <li>All fees are non-refundable except as described in our refund policy below.</li>
                <li>We reserve the right to change pricing with 30 days' notice to existing subscribers.</li>
                <li>If a payment fails, your deployment data and configurations will be retained for 7 days. After this period, your data may be permanently deleted.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>8. Refund Policy</h2>
              <p>
                Only the unused portion of your subscription is eligible for a refund. Metered usage (compute, bandwidth, AI gateway tokens, etc.) — including usage incurred by an agent acting on your behalf — is non-refundable. Refund requests must be submitted within 30 days of purchase.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>9. Beta Status and Service Availability</h2>
              <p>
                <strong>tokendance.net does not offer a Service Level Agreement (SLA).</strong> We make no contractual guarantees about uptime, availability, response times, or recovery times. There are no service credits for downtime.
              </p>
              <p>
                tokendance.net is currently offered as a beta service. During the beta period:
              </p>
              <ul>
                <li><strong>We reserve the right to suspend, restrict, modify, or shut down the Service — including any free tier, free trial, or promotional access — at any time, with or without notice, for any individual account or for the platform as a whole.</strong></li>
                <li>Features may change, be removed, or be gated behind paid plans without prior warning.</li>
                <li>Use of the platform during beta is at your own risk.</li>
                <li>We will make a reasonable effort to give notice and a data export window before any platform-wide shutdown, but this is not contractually guaranteed.</li>
              </ul>
              <p>
                We will make best-effort attempts to keep the platform available, but we are not liable for any downtime, data loss, or service interruptions, including those caused by infrastructure provider outages, third-party AI model or agent vendor outages, natural disasters, or cyberattacks.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>10. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, tokendance.net and its team shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising out of or related to (a) your use of the Service, (b) actions taken by AI coding agents using your tokens, or (c) the output of any third-party AI model or agent integrated with the Service. Our total liability for any claim shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>11. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless tokendance.net, its officers, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your use of the Service, agent activity authorized by your tokens, your violation of these Terms, or your violation of any rights of a third party.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>12. Termination</h2>
              <p>
                We may suspend or terminate your account at any time for violation of these Terms or for any other reason at our discretion, with or without notice. Upon termination, your right to use the Service ceases immediately and all issued agent tokens are revoked. You may delete your account at any time by contacting us. Provisions that by their nature should survive termination (including intellectual property, agent-action responsibility, limitation of liability, and indemnification) will remain in effect.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>13. Modifications to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Material changes will be communicated via email or a prominent notice on our website at least 14 days before they take effect. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>14. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which tokendance.net operates, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved through binding arbitration or in the courts of competent jurisdiction.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>15. Contact Us</h2>
              <p>
                If you have questions about these Terms of Service, please contact us at <a href="mailto:hi@tokendance.net">hi@tokendance.net</a>.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  )
}
